import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Building2, SlidersHorizontal, ShieldCheck, Files, History, Settings2, Truck, Wrench, FolderKanban, UsersRound, CalendarRange, BadgeCheck, ScanText, Workflow, NotebookPen, Receipt, LayoutDashboard, Boxes, PiggyBank, Bell, Bot, ShieldAlert, Smartphone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import API from '../../lib/api';
import { ErpStatus } from './ErpShared';
import './erp.css';

const links = [
  ['dashboards', 'Dashboard', LayoutDashboard, 'erp.access'],
  ['copilot', 'Copilot AI', Bot, 'erp.access'],
  ['notifications', 'Notificări', Bell, 'notifications.read'],
  ['organization', 'Organizație', Building2, 'company.read'], ['catalogs', 'Nomenclatoare', SlidersHorizontal, 'catalogs.read'],
  ['projects', 'Proiecte', FolderKanban, 'projects.read'], ['extraction', 'Extragere AI', ScanText, 'extraction.read'],
  ['workflows', 'Fluxuri de lucru', Workflow, 'workflows.read'],
  ['diary', 'Jurnal de șantier', NotebookPen, 'diary.read'],
  ['invoicing', 'Facturare', Receipt, 'invoicing.read'],
  ['materials', 'Materiale și stocuri', Boxes, 'materials.read'],
  ['budgets', 'Bugete și costuri', PiggyBank, 'budgets.read'],
  ['risk', 'Riscuri', ShieldAlert, 'risk.read'],
  ['people', 'Echipe', UsersRound, 'people.read'], ['allocation', 'Alocări', CalendarRange, 'allocation.read'],
  ['fleet', 'Parc Auto', Truck, 'fleet.read'], ['equipment', 'Utilaje', Wrench, 'equipment.read'],
  ['compliance', 'Conformitate', BadgeCheck, 'compliance.read'],
  ['roles', 'Roluri și acces', ShieldCheck, 'roles.read'], ['documents', 'Documente', Files, 'documents.read'],
  ['mobile', 'Aplicație mobilă', Smartphone, 'erp.access'],
  ['modules', 'Module', Settings2, 'flags.read'], ['audit', 'Audit', History, 'audit.read']
];

export default function ErpLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [ctx, setCtx] = useState(null);
  const [failed, setFailed] = useState(false);
  const refresh = useCallback(() => {
    setFailed(false); API.get('/erp/context').then(r => setCtx(r.data)).catch(() => setFailed(true));
  }, []);
  useEffect(() => { refresh(); }, [refresh, user]);
  if (!user?.erp_permissions?.includes('erp.access')) return <div className="p-8 text-muted-foreground" data-testid="erp-forbidden">Nu ai acces la spațiul ERP. Contactează administratorul.</div>;
  if (!ctx || failed) return <ErpStatus list={{ loading: !failed, error: failed ? 'Datele ERP nu au putut fi încărcate.' : '', reload: refresh }} />;
  const enabled = new Set(ctx.modules.map(m => m.code));
  const visible = links.filter(([key, , , permission]) => ctx.permissions.includes(permission) && (key === 'modules' || enabled.has(key)));
  const current = location.pathname.split('/')[2];
  if (!current) return visible.length ? <Navigate to={`/erp/${visible[0][0]}`} replace /> : <p className="p-8" data-testid="erp-no-modules">Nu există module active pentru rolul tău.</p>;
  const allowed = visible.some(l => l[0] === current);
  return <div className="erp-shell" data-testid="erp-shell">
    <div className="erp-brand-band"><div><p className="text-xs text-primary mb-1">SAGA · OPERAȚIUNI</p><p className="text-base font-medium" data-testid="erp-company-name">{ctx.company_name}</p></div><span className="text-xs text-muted-foreground" data-testid="erp-scope-label">Spațiu ERP</span></div>
    <nav className="erp-tabs" aria-label="Navigare ERP">{visible.map(([key, label, Icon]) => <Link key={key} to={`/erp/${key}`} data-testid={`erp-nav-${key}`} className={`erp-tab ${current === key ? 'active' : ''}`}><Icon className="w-4 h-4 shrink-0" />{label}</Link>)}</nav>
    <div className="erp-content">{allowed ? <Outlet context={{ ...ctx, refresh }} /> : <p role="alert" data-testid="erp-module-forbidden">Modul dezactivat sau acces nepermis.</p>}</div>
  </div>;
}